import {
  WizardEmployeeChoiceStep,
  WizardDateChoiceStep,
  WizardChoiceListStep,
  WizardTextAreaStep,
  WizardTextAreaChoiceStep,
  WizardFactsListStep,
  WizardRangeStep,
  WizardGoalsChoiceStep,
  WizardCompanyValuesChoiceStep,
  WizardChatbotText,
  WizardChatbotChoice,
  WizardChatbotMultipleText,
  WizardStep as ReactWizardStep,
} from '@/features/wizard';
import {
  ComponentReturnTypeMap,
  WizardStep as DomainWizardStep,
  WizardAnswers,
  FeedbackFormData,
  PraiseFormData,
  DebriefFormData,
  Employee,
  CompanyValue,
  InteractionType,
  AllGoalTypes,
} from '@momentum/types';
import { getIconForOption } from '@/features/wizard/utils/wizard-icons';
import { GoalOption } from '@/features/wizard/components/WizardGoalsChoiceStep';
import { CompanyValueOption } from '@/features/wizard/components/WizardCompanyValuesChoiceStep';
import { WizardChatbotSignals } from '@/features/wizard/components/WizardChatbotSignals';
// Data fetcher types
interface FetchData {
  'fetch-employees': () => Employee[];
  'fetch-goals': () => GoalOption[];
  'fetch-company-values': () => CompanyValueOption[];
}

// Prop types for better type safety
interface StaticProp {
  type: 'static';
  data: unknown;
}

interface FetchProp {
  type: 'fetch';
  data: keyof FetchData;
}

interface PreviousAnswerProp {
  type: 'previous-answer';
  data: {
    answersStepId: string;
    titleField?: string;
    idField?: string;
  };
}

type PropType = StaticProp | FetchProp | PreviousAnswerProp;

// Component mapping from domain component IDs to React components
const componentMap: Record<
  keyof ComponentReturnTypeMap,
  React.ComponentType<any>
> = {
  EmployeeSelection: WizardEmployeeChoiceStep,
  MultiEmployeeSelection: WizardEmployeeChoiceStep,
  DateSelection: WizardDateChoiceStep,
  ChoiceList: WizardChoiceListStep,
  EffectsChoiceList: WizardChoiceListStep,
  TextArea: WizardTextAreaStep,
  TextAreaChoice: WizardTextAreaChoiceStep,
  FactsList: WizardFactsListStep,
  Range: WizardRangeStep,
  GoalsChoice: WizardGoalsChoiceStep,
  CompanyValuesChoice: WizardCompanyValuesChoiceStep,
  ChatbotText: WizardChatbotText,
  ChatbotChoice: WizardChatbotChoice,
  ChatbotMultipleText: WizardChatbotMultipleText,
  WizardChatbotSignals: WizardChatbotSignals,
} as const;

type ComponentId = keyof typeof componentMap;
type ComponentClass = (typeof componentMap)[ComponentId];

// Helper function to resolve function titles with recipient data
export const resolveFunctionTitle = (
  title:
    | string
    | (({
        answers,
        recipient,
      }: {
        answers: WizardAnswers<
          FeedbackFormData | PraiseFormData | DebriefFormData
        >;
        sender: Employee;
        recipient?: Employee;
      }) => string),
  answers: WizardAnswers<FeedbackFormData | PraiseFormData | DebriefFormData>,
  user: Employee
): string => {
  if (typeof title === 'function' && 'employee-selection' in answers) {
    const recipient = answers['employee-selection'] as Employee;
    return title({ recipient, answers, sender: user });
  }

  if (typeof title === 'function' && 'debrief-leader' in answers) {
    const recipient = answers['debrief-leader'] as Employee;
    return title({ recipient, answers, sender: user });
  } else if (typeof title === 'function') {
    return title({ answers, sender: user });
  }

  return String(title);
};

// Create data fetchers
export const createFetchData = (
  employees: Employee[],
  goals: AllGoalTypes[],
  companyValues: CompanyValue[]
): FetchData => ({
  'fetch-employees': () => employees,
  'fetch-goals': () =>
    goals.map((goal) => ({
      id: goal._id,
      title: goal.title,
      description: goal.description,
    })),
  'fetch-company-values': () =>
    companyValues.map((value) => ({
      id: value._id,
      title: value.value,
    })),
});

export const createPropsResolver = (fetchData: FetchData) => {
  const resolveProp = (
    prop: PropType,
    t: any,
    answers?: WizardAnswers
  ): unknown => {
    if (!prop) return undefined;

    if (prop.type === 'static') {
      if (
        Array.isArray(prop.data) &&
        prop.data.length > 0 &&
        typeof (prop.data[0] as Record<string, unknown>)?.title === 'string'
      ) {
        return prop.data.map((option: Record<string, unknown>) => ({
          ...option,
          title: t(option.title as string),
          description: option.description
            ? t(option.description as string)
            : undefined,
          // Convert emoji icons to React icon components
          icon: option.icon
            ? getIconForOption(option.icon as string)
            : undefined,
        }));
      }

      // If it's a string that looks like a translation key, translate it
      if (typeof prop.data === 'string' && prop.data.startsWith('wizard:')) {
        return t(prop.data);
      }

      return prop.data;
    }

    if (prop.type === 'fetch') {
      const fetcher = fetchData[prop.data];
      if (fetcher) {
        return fetcher();
      }
    }

    if (prop.type === 'previous-answer') {
      if (!answers) return [];

      const { answersStepId, titleField = 'text', idField = 'id' } = prop.data;
      const previousAnswer = answers[answersStepId];

      if (!previousAnswer) return [];

      let options: Array<{ id: string; title: string }> = [];

      // Auto-detect shape
      if (Array.isArray(previousAnswer)) {
        // Array of objects or strings
        options = previousAnswer.map((item: unknown, index: number) => {
          if (typeof item === 'string') {
            return { id: index.toString(), title: item };
          }
          const itemObj = item as Record<string, unknown>;
          return {
            id: (itemObj?.[idField] as string) ?? index.toString(),
            title: (itemObj?.[titleField] as string) ?? String(item),
          };
        });
      } else if (
        typeof previousAnswer === 'object' &&
        previousAnswer !== null
      ) {
        // Object map -> entries
        options = Object.entries(previousAnswer as Record<string, unknown>).map(
          ([key, value]) => ({
            id: key,
            title: String(value),
          })
        );
      } else if (typeof previousAnswer === 'string') {
        options = [{ id: 'single', title: previousAnswer }];
      }

      return options;
    }

    return undefined;
  };

  const resolveProps = (
    props: Record<string, PropType>,
    t: any,
    answers?: WizardAnswers
  ) => {
    const resolved: Record<string, unknown> = {};

    Object.entries(props).forEach(([key, prop]) => {
      if (prop.type === 'previous-answer') {
        // Dynamic resolution with current answers
        resolved[key] = answers ? resolveProp(prop, t, answers) : prop;
      } else {
        // Static resolution
        resolved[key] = resolveProp(prop, t);
      }
    });

    return resolved;
  };

  return { resolveProp, resolveProps };
};

// Special prop mapping for components that use different prop names
export const getComponentWithProps = (
  componentId: ComponentId,
  ComponentClass: ComponentClass,
  resolvedProps: Record<string, unknown>,
  resolvePropFn: ReturnType<typeof createPropsResolver>['resolveProp'],
  t: any,
  user: Employee,
  interactionType?: InteractionType
): ComponentClass => {
  // Handle ChatbotChoice with choices array
  if (componentId === 'ChatbotChoice') {
    return (props: Record<string, unknown>) => {
      // Resolve nested choices structure
      const resolvedChoices = (
        resolvedProps.choices as Record<string, unknown>[]
      )?.map((choice) => ({
        question: choice.question ? t(choice.question) : undefined,
        // the options prop has a previous-answer type, so we need to resolve it dynamically
        options: resolvePropFn(choice.options as PropType, t),
        multiSelect: choice.multiSelect,
      }));

      return (
        <ComponentClass
          {...props}
          {...resolvedProps}
          choices={resolvedChoices}
        />
      );
    };
  }
  // Handle employee selection with special props
  if (
    componentId === 'EmployeeSelection' ||
    componentId === 'MultiEmployeeSelection'
  ) {
    return (props: Record<string, unknown>) => (
      <ComponentClass
        {...props}
        {...resolvedProps}
        employees={resolvedProps.options}
      />
    );
  }

  // Handle goals selection with special props
  if (componentId === 'GoalsChoice') {
    return (props: Record<string, unknown>) => (
      <ComponentClass
        {...props}
        {...resolvedProps}
        goals={resolvedProps.options}
        columns={resolvedProps.columns}
        variant={resolvedProps.variant}
      />
    );
  }

  // Handle company values with special props
  if (componentId === 'CompanyValuesChoice') {
    return (props: Record<string, unknown>) => (
      <ComponentClass
        {...props}
        {...resolvedProps}
        companyValues={resolvedProps.options}
        columns={resolvedProps.columns}
        variant={resolvedProps.variant}
      />
    );
  }

  // Handle components with options that might have function titles or previous-answer dependencies
  if (resolvedProps.options) {
    return (props: { answers: WizardAnswers }) => {
      let finalOptions = resolvedProps.options;

      // Handle previous-answer type options
      if ((resolvedProps.options as PropType).type === 'previous-answer') {
        finalOptions = resolvePropFn(
          resolvedProps.options as PropType,
          t,
          props.answers
        );
      }

      // Handle dynamic function titles
      if (Array.isArray(finalOptions)) {
        const hasFunctionsTranslations = finalOptions.some(
          (opt: { title: string; description: string }) =>
            typeof opt.title === 'function'
        );

        if (hasFunctionsTranslations && props.answers) {
          finalOptions = finalOptions.map((option) => ({
            ...option,
            title: resolveFunctionTitle(
              option.title,
              props.answers as WizardAnswers<
                PraiseFormData | DebriefFormData | FeedbackFormData
              >,
              user
            ),
          }));
        }
      }

      return (
        <ComponentClass {...props} {...resolvedProps} options={finalOptions} />
      );
    };
  }

  // Handle TextArea with interaction type
  if (componentId === 'TextArea') {
    return (props: Record<string, unknown>) => (
      <ComponentClass
        {...props}
        {...resolvedProps}
        interactionType={interactionType || InteractionType.FEEDBACK}
      />
    );
  }

  // Default component
  return (props: Record<string, unknown>) => (
    <ComponentClass {...props} {...resolvedProps} />
  );
};

// Helper function to convert a domain step to a React step
export const convertDomainStepToReactStep = <
  TFormData extends DebriefFormData | PraiseFormData | FeedbackFormData
>(
  step: DomainWizardStep<TFormData>,
  resolver: ReturnType<typeof createPropsResolver>,
  t: any,
  user: Employee
): ReactWizardStep<TFormData> => {
  const ComponentClass = componentMap[step.component.id as ComponentId];

  if (!ComponentClass) {
    throw new Error(`Unknown component type: ${step.component.id}`);
  }

  const { resolveProp, resolveProps } = resolver;

  // Resolve props statically at step creation time
  const resolvedProps = step.component.props
    ? resolveProps(step.component.props, t) // Static resolution
    : {};

  // Steps with manual Next buttons get showNextInFooter so WizardFlow
  // renders the Next button in the card footer alongside Previous.
  const manualNextComponents = new Set<string>([
    'TextArea',
    'TextAreaChoice',
    'FactsList',
    'MultiEmployeeSelection',
  ]);
  // ChoiceList only needs footer Next in multi-select mode
  const isMultiSelectChoiceList =
    step.component.id === 'ChoiceList' &&
    (step.component.props?.multiSelect as { type: string; data: unknown })
      ?.data === true;
  const showNextInFooter =
    manualNextComponents.has(step.component.id) || isMultiSelectChoiceList;

  return {
    ...step,
    showNextInFooter,
    title: (answers) => {
      let translationPath: string;
      // some of the step titles are functions that return the translation path (for dynamic phrasing based on previous answers)
      if (typeof step.title === 'function') {
        translationPath = step.title(answers);
      } else {
        translationPath = step.title;
      }
      // some of the translated path are functions themselves that need the recipient data to generate the title
      return resolveFunctionTitle(t(translationPath), answers, user);
    },
    description: step.description ? t(step.description) : undefined,
    component: getComponentWithProps(
      step.component.id,
      ComponentClass,
      resolvedProps,
      // passing it for props that need to be resolved dynamically (based on previous answers)
      resolveProp,
      t,
      user
    ),
    validation: step.validation
      ? (answer) => {
          const result = step.validation?.(answer);
          return result ? t(result) : null;
        }
      : undefined,
  };
};
